import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useField, FormikValues} from 'formik';

import {requestHighSchool} from '../actions/missing-high-school-modal';
import Modal from 'shared/components/modal';
import AssetService from 'src/services/AssetService';
import RequestHighSchoolModal from './request-high-school-modal';
import FindInternationalHighSchoolModal from './find-international-high-school-modal';
import HighSchoolRequestedModal from './high-school-requested-modal';

interface CountryOption {
  value: string;
  text: string;
}

interface Props {
  modal?: string | null;
  setHighSchoolModal: (modal: string | null) => void;
  onSubmitModal?: () => void;
  fieldName: string;
}

const MissingHighSchoolModal = ({
  modal,
  setHighSchoolModal,
  onSubmitModal = () => {},
  fieldName,
}: Props) => {
  const dispatch = useDispatch();
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [field, _meta, helpers] = useField(fieldName);

  useEffect(() => {
    AssetService.getEligibleCountryOptions().then((options) =>
      setCountries(options as unknown as CountryOption[])
    );
  }, []);

  const hideModal = () => setHighSchoolModal(null);

  const switchModalCountry = (country: string) => {
    setHighSchoolModal(country === 'US' ? 'request-high-school' : 'find-international-high-school');
  };

  const onSwitchCountry = (country: string) => {
    helpers.setValue({
      ...field.value,
      country,
      city: '',
    });

    switchModalCountry(country);
  };

  const handleSubmit = (data: FormikValues) =>
    // @ts-expect-error untyped action file
    dispatch(requestHighSchool(data)).then(() => {
      setHighSchoolModal('school-requested');
    });

  const renderModalContents = (modalName?: string) => {
    switch (modalName) {
      case 'request-high-school':
        return renderRequestHighSchoolModal();
      case 'school-requested':
        return renderHighSchoolRequestedModal();
      case 'find-international-high-school':
        return renderFindInternationalHighSchoolModal();
      default:
        return null;
    }
  };

  const ariaLabelFor = (modalName?: string) =>
    ({
      'request-high-school': 'Send a Request',
      'school-requested': 'Request sent',
      'find-international-high-school': 'Check for Your High School',
    })[modalName || ''];

  const renderRequestHighSchoolModal = () => (
    <RequestHighSchoolModal
      requestHighSchool={{
        country: field.value.country,
        subdivision: field.value.subdivision,
        city: field.value.city,
        schoolName: '',
      }}
      handleSubmit={handleSubmit}
      onSwitchCountry={onSwitchCountry}
      countries={countries}
      hideModal={hideModal}
    />
  );

  const renderFindInternationalHighSchoolModal = () => (
    <FindInternationalHighSchoolModal
      countries={countries}
      onSwitchCountry={switchModalCountry}
      hideModal={hideModal}
      onSubmitModal={onSubmitModal}
      fieldName={fieldName}
    />
  );

  const renderHighSchoolRequestedModal = () => <HighSchoolRequestedModal hideModal={hideModal} />;

  return (
    <Modal
      aria-label={ariaLabelFor(modal ?? undefined)}
      show={!!modal}
      onClose={hideModal}
      className={`missing-high-school-modal ${modal}-modal`}
    >
      {renderModalContents(modal ?? undefined)}
    </Modal>
  );
};

export default MissingHighSchoolModal;
